import axios from 'axios'
import { ServiceError, getApiErrorStatus } from '@/utils/apiError'
import { CepResponse } from '../Types/Responses/CepResponses'

/**
 * Mensagens da busca de CEP, compartilhadas pela consulta direta ao ViaCEP
 * (`SearchCep`) e pelo proxy do backend (`ConsultingAddressService`).
 * Falha de CEP é degradável: as duas orientam o preenchimento manual, mas
 * distinguem "o CEP não existe" (o usuário confere o número) de "o serviço não
 * respondeu" (o número pode estar certo).
 */
export const CEP_NOT_FOUND_MESSAGE =
  'CEP não encontrado. Confira o número ou preencha o endereço manualmente.'
export const CEP_LOOKUP_UNAVAILABLE_MESSAGE =
  'Não foi possível buscar o endereço pelo CEP agora. Preencha o endereço manualmente.'

type ViaCepPayload = Partial<CepResponse> & { erro?: boolean | string }

/**
 * Consulta direta ao ViaCEP (serviço externo, fora da API interna).
 *
 * O ViaCEP responde 200 com `{ erro: true }` (ou `"true"`, nas versões mais
 * novas) para CEP inexistente e 400 para CEP mal formatado — os dois viram
 * "CEP não encontrado". Rede, timeout e 5xx viram "serviço indisponível".
 */
export const SearchCep = async (cep: string): Promise<CepResponse> => {
  let data: ViaCepPayload | undefined
  try {
    const response = await axios.get<ViaCepPayload>(`https://viacep.com.br/ws/${cep}/json/`)
    data = response.data
  } catch (e) {
    if (getApiErrorStatus(e) === 400) throw new ServiceError(CEP_NOT_FOUND_MESSAGE, e)
    throw new ServiceError(CEP_LOOKUP_UNAVAILABLE_MESSAGE, e)
  }

  if (data?.erro === true || data?.erro === 'true') {
    throw new ServiceError(CEP_NOT_FOUND_MESSAGE, data)
  }
  // Corpo fora do contrato (HTML de erro de proxy, resposta vazia): não dá para
  // confiar no endereço, então trata como indisponibilidade.
  if (!data || typeof data !== 'object' || !data.cep) {
    throw new ServiceError(CEP_LOOKUP_UNAVAILABLE_MESSAGE, data)
  }

  return data as CepResponse
}
